import { Loader2, Check, Circle, AlertTriangle, ChevronRight } from "lucide-react";

const STAGE_LABELS = {
  agents: "Agents",
  consensus: "Consensus",
  root_cause: "Root cause",
  guardrail: "Guardrail",
  runbook: "Runbook",
};

const STATE_TONE = {
  pending: "border-white/10 text-zinc-500 bg-black/20",
  running: "border-blue-500/40 text-blue-300 bg-blue-500/[0.08]",
  done: "border-emerald-500/40 text-emerald-300 bg-emerald-500/[0.06]",
  failed: "border-rose-500/40 text-rose-300 bg-rose-500/[0.08]",
};

function StateIcon({ state }) {
  if (state === "done")
    return <Check className="h-3 w-3" strokeWidth={2.5} />;
  if (state === "running")
    return <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />;
  if (state === "failed")
    return <AlertTriangle className="h-3 w-3" strokeWidth={2} />;
  return <Circle className="h-3 w-3" strokeWidth={1.75} />;
}

/**
 * Inline pipeline progress strip rendered while `status === "analyzing"`.
 * Shows each pipeline stage (Agents, Consensus, Root cause, Guardrail,
 * Runbook) and its current state so the on-call sees exactly which stage
 * is in flight instead of a generic spinner.
 */
export default function PipelineProgress({ stages }) {
  if (!stages || !stages.length) return null;

  const total = stages.length;
  const done = stages.filter((s) => s.state === "done").length;
  const failed = stages.filter((s) => s.state === "failed").length;
  const running = stages.filter((s) => s.state === "running").length;
  const pct = Math.round((done / total) * 100);

  return (
    <div
      data-testid="pipeline-progress"
      className="cre-glass border border-white/10 rounded-sm p-3 flex items-center gap-2 flex-wrap"
    >
      <span className="cre-overline text-blue-300 mr-1">Pipeline</span>

      <div className="flex items-center gap-1 flex-wrap" data-testid="pipeline-stages">
        {stages.map((stage, idx) => (
          <div key={stage.key} className="flex items-center">
            <span
              data-testid={`pipeline-stage-${stage.key}`}
              data-state={stage.state}
              title={
                stage.detail ||
                (stage.state === "running"
                  ? "in flight"
                  : stage.state === "done"
                  ? "complete"
                  : stage.state === "failed"
                  ? "failed — check audit trail"
                  : "pending")
              }
              className={`inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border transition-colors ${STATE_TONE[stage.state] || STATE_TONE.pending}`}
            >
              <StateIcon state={stage.state} />
              {STAGE_LABELS[stage.key] || stage.key}
            </span>
            {idx < stages.length - 1 ? (
              <ChevronRight
                className="h-3 w-3 text-zinc-700 mx-0.5 shrink-0"
                strokeWidth={2}
              />
            ) : null}
          </div>
        ))}
      </div>

      <span
        data-testid="pipeline-progress-summary"
        className="ml-auto text-[11px] font-mono text-zinc-500"
      >
        {failed > 0
          ? `${done}/${total} done · ${failed} failed`
          : running > 0
          ? `${done}/${total} done · ${running} live · ${pct}%`
          : `${done}/${total} done · ${pct}%`}
      </span>
    </div>
  );
}
